/**
 * UC-005 Self-service David A&L soft executive view (Phase 23).
 *
 * Type contracts pro `/api/tenant/dashboard/overview` REST endpoint a podpůrné
 * dataset typy pro `<TimelineChart>` + `<SpzBarChart>` + `<ClosedMonthsTable>`.
 *
 * REQ-BIZ-021/023 + princip #2 — CZK + EUR paralelně bez FX přepočtu, source of
 * truth = Axigon. EUR widget renderuje pouze pokud `sum_eur_minor > 0`.
 *
 * REQ-API-003 — endpoint je query-only (GET), žádné mutace; backend RLS scope
 * (princip #6) přes tenant cookie session.
 */

/**
 * Per-měsíc per-SPZ datapoint pro <TimelineChart>. `amount_kc` je plain decimal
 * string (backend posílá `NUMERIC(12,2)` jako string aby JS nepokazil precision).
 */
export interface TimelinePoint {
  /** ISO YYYY-MM (např. "2026-04"). */
  month: string;
  /** SPZ plate text — slouží jako Line key v Recharts. */
  plate_text: string;
  amount_kc: string;
  amount_eur: string;
}

/** Souhrn jednoho uzavřeného měsíce pro ClosedMonthsTable. */
export interface ClosedMonthRow {
  budget_id: string;
  month: string;
  billing_group_code: string;
  total_kc: string;
  total_eur: string;
  spz_count: number;
  transaction_count: number;
  /** Pokud non-null → composite PDF k dispozici. */
  composite_pdf_id: string | null;
  /** Statement-set complete flag z UC-008 (REQ-BIZ-022). */
  statement_set_complete: boolean;
  attached_invoice_statement_count: number;
}

/** Per-karta agregát napříč period pro <SpzBarChart>. plate_text = SPZ popisek. */
export interface SpzBarPoint {
  fuel_card_id: string;
  plate_text: string;
  total_kc: string;
  total_eur: string;
  transaction_count: number;
}

export interface TenantDashboardOverviewResponse {
  tenant_id: string;
  billing_group_code: string;
  /** Role detect — endpoint vrací pouze tenant rozsah; cross-tenant blok = RLS. */
  is_a_l_soft: boolean;
  /** Plain-decimal strings — REQ-BIZ-021 stat cards. */
  consumption_total_kc: string;
  payments_total_kc: string;
  outstanding_total_kc: string;
  /** Souhrn EUR napříč obdobím — pokud "0" → EUR widget skrytý. */
  consumption_total_eur: string;
  /** Time range použitý pro consumption stats: "6M" | "12M" | "ALL". */
  time_range: string;
  /** Timeline datapoint per (month, plate) — backend group BY. */
  timeline: TimelinePoint[];
  /** Per-SPZ agregát pro `<SpzBarChart>`. */
  spz_breakdown: SpzBarPoint[];
  /** Uzavřené měsíce pro `<ClosedMonthsTable>`. */
  closed_months: ClosedMonthRow[];
}

export const TIME_RANGE_OPTIONS = ["6M", "12M", "ALL"] as const;
export type TimeRange = (typeof TIME_RANGE_OPTIONS)[number];

export const EXPORT_FORMATS = ["xlsx", "csv", "json"] as const;
export type ExportFormat = (typeof EXPORT_FORMATS)[number];

/**
 * Plain-decimal string → cs-CZ formátovaný "1 234,56 Kč" / "—".
 * Žádný FX přepočet — princip #2 source-of-truth.
 */
export function formatKcDashboard(raw: string | null | undefined): string {
  if (raw == null || raw === "") return "—";
  const n = Number(raw);
  if (!Number.isFinite(n)) return raw;
  return (
    n.toLocaleString("cs-CZ", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + " Kč"
  );
}

export function formatEurDashboard(raw: string | null | undefined): string {
  if (raw == null || raw === "") return "—";
  const n = Number(raw);
  if (!Number.isFinite(n)) return raw;
  return (
    n.toLocaleString("cs-CZ", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + " €"
  );
}

/** Numericky-bezpečné porovnání plain-decimal string > 0. */
export function eurIsPositive(raw: string | null | undefined): boolean {
  if (raw == null || raw === "") return false;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0;
}
